import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, MapPin, Clock, Phone, ShieldCheck, Star,
  Share2, Copy, Loader2, Languages, MessageCircle
} from "lucide-react";
import DoctorDirectoryCard from "@/components/DoctorDirectoryCard";
import VerifiedBadge, { useVerifiedSeoMeta } from "@/components/VerifiedBadge";
import { useI18n } from "@/contexts/I18nContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_ORIGIN = window.location.origin;

function setMeta(name, content, property = false) {
  if (!content) return;
  const attr = property ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Public shareable Hospital / Clinic profile page.
 * URL: /hospital/{slug} — no auth, SEO-friendly meta tags, mobile-responsive.
 */
export default function PublicHospitalSharePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, setLang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/public/hospital/${slug}`);
        if (!alive) return;
        setData(data);
      } catch (e) {
        if (!alive) return;
        setErr(e.response?.status === 404 ? "Hospital not found." : "Failed to load profile.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const s = data.share || {};
    document.title = s.title ? `${s.title} · UpchaarQ` : "Hospital · UpchaarQ";
    setMeta("description", s.description || "");
    setMeta("og:title", s.title, true);
    setMeta("og:description", s.description, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", `${SITE_ORIGIN}/hospital/${slug}`, true);
    if (s.image) setMeta("og:image", s.image, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", s.title);
    setMeta("twitter:description", s.description);
    if (s.image) setMeta("twitter:image", s.image);
  }, [data, slug]);

  const shareLink = `${SITE_ORIGIN}/hospital/${slug}`;
  const isHi = lang === "hi";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(isHi ? "लिंक कॉपी हो गया" : "Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const shareNative = async () => {
    const s = data?.share || {};
    if (navigator.share) {
      try {
        await navigator.share({ title: s.title, text: s.description, url: shareLink });
      } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  const shareWhatsApp = () => {
    const text = data?.share?.title || "Book here";
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${shareLink}`)}`, "_blank");
  };

  const h = data?.hospital || {};
  const r = data?.rating || {};
  const doctors = data?.doctors || [];
  const reviews = data?.reviews || [];

  useVerifiedSeoMeta({
    verified: !!h.cmho_verified,
    entityName: h.name,
    verifiedAt: h.cmho_verified_at,
    url: `${SITE_ORIGIN}/hospital/${slug}`,
  });

  if (loading && !data) {
    return (
      <div className="min-h-screen min-h-screen-safe flex items-center justify-center text-slate-500 gap-2" data-testid="public-hospital-loading">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading hospital…
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen min-h-screen-safe flex flex-col items-center justify-center px-4 text-center">
        <p className="text-slate-500 mb-4" data-testid="public-hospital-error">{err}</p>
        <Button variant="outline" onClick={() => navigate("/")}>Go home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-h-screen-safe bg-slate-50" data-testid="public-hospital-page">
      {/* Hero */}
      <header className="bg-gradient-to-br from-slate-900 via-sky-800 to-emerald-700 text-white app-header-safe">
        <div className="max-w-6xl mx-auto px-5 py-8 sm:py-12 px-safe">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold">
              {h.type || "Hospital"}
            </div>
            <button onClick={() => setLang(isHi ? "en" : "hi")}
              data-testid="hospital-lang-toggle"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full px-3 py-1 text-xs">
              <Languages className="w-3.5 h-3.5" /> {isHi ? "English" : "हिंदी"}
            </button>
          </div>
          <div className="flex items-start gap-5 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white/15 ring-4 ring-white/20 overflow-hidden flex items-center justify-center shrink-0 backdrop-blur">
              {h.image ? (
                <img src={h.image} alt={h.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-12 h-12 text-white/80" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="hospital-name">{h.name}</h1>
                <VerifiedBadge
                  verified={!!h.cmho_verified}
                  verifiedAt={h.cmho_verified_at}
                  variant="solid"
                  className="ml-1"
                />
                {h.verified && !h.cmho_verified && (
                  <Badge className="bg-emerald-400/30 text-white border border-white/30 gap-1" data-testid="hospital-verified-badge">
                    <ShieldCheck className="w-3 h-3" /> Verified
                  </Badge>
                )}
              </div>
              <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-white/95">
                {r.count > 0 && (
                  <span className="inline-flex items-center gap-1 bg-amber-400/25 px-2 py-0.5 rounded-full" data-testid="hospital-rating">
                    <Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" />
                    <b>{r.avg?.toFixed(1)}</b>
                    <span className="opacity-80">({r.count})</span>
                  </span>
                )}
                {doctors.length > 0 && (
                  <span className="opacity-90">
                    {doctors.length} {isHi ? "डॉक्टर" : `doctor${doctors.length !== 1 ? "s" : ""}`}
                  </span>
                )}
              </div>
              <div className="mt-3 space-y-1 text-sm text-white/85">
                {h.address && (
                  <p className="flex items-start gap-1.5" data-testid="hospital-address">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{h.address}{h.city ? `, ${h.city}` : ""}</span>
                  </p>
                )}
                {h.working_hours && (
                  <p className="flex items-center gap-1.5" data-testid="hospital-hours">
                    <Clock className="w-4 h-4" /> {h.working_hours}
                  </p>
                )}
                {h.phone && (
                  <p className="flex items-center gap-1.5">
                    <Phone className="w-4 h-4" /> {h.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                data-testid="hospital-share-btn"
                onClick={shareNative}
                className="bg-white/95 text-sky-700 hover:bg-white gap-1.5">
                <Share2 className="w-4 h-4" /> {isHi ? "शेयर" : "Share"}
              </Button>
              <Button
                data-testid="hospital-share-copy"
                onClick={copyLink}
                variant="ghost"
                className="text-white hover:bg-white/10 gap-1.5">
                <Copy className="w-3.5 h-3.5" /> Copy link
              </Button>
              <Button
                data-testid="hospital-share-whatsapp"
                onClick={shareWhatsApp}
                className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8 space-y-8">
        {/* Doctor directory */}
        <section data-testid="doctor-directory">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                {isHi ? "हमारे डॉक्टर" : "Our Doctors"}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {isHi ? "एक क्लिक में अपनी अपॉइंटमेंट बुक करें" : "Book your appointment in a single click"}
              </p>
            </div>
          </div>
          {doctors.length === 0 ? (
            <Card className="medical-card p-8 text-center text-slate-500" data-testid="doctor-directory-empty">
              {isHi ? "अभी कोई डॉक्टर उपलब्ध नहीं" : "No doctors are currently available at this hospital."}
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {doctors.map((d) => (
                <DoctorDirectoryCard key={d.id} doctor={d} lang={lang} />
              ))}
            </div>
          )}
        </section>

        {/* Public Reviews Wall */}
        {reviews.length > 0 && (
          <section data-testid="hospital-reviews-wall">
            <div className="mb-3">
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                {isHi ? "मरीज़ों की राय" : "What patients are saying"}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                {isHi ? "टॉप 3 अनाम फीडबैक" : "Top 3 anonymised patient reviews"}
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {reviews.map((rv, i) => (
                <Card key={i} className="medical-card p-5" data-testid={`hospital-review-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white font-heading font-semibold text-sm">
                      {rv.initials}
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star key={k} className={`w-3.5 h-3.5 ${k < rv.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">"{rv.comment}"</p>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
